import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Grid3x3, ShieldCheck, ScanSearch, Calculator, KeyRound, ArrowRight, Loader2 } from "lucide-react";
import useAuth from "../hooks/useAuth";
import { authApi } from "../lib/auth";
import { errorMessage } from "../lib/api";
import { Input } from "../components/ui/input";

const PERKS = [
  { icon: ScanSearch, title: "Scan your Microsoft tenants", text: "Pull live license counts, users and Conditional Access policies through Graph." },
  { icon: Calculator, title: "Plan the target state", text: "E3 to E5, or E3 + Defender + Purview - see delta cost, feature gains and add-ons you can retire." },
  { icon: ShieldCheck, title: "Built for admins", text: "Role-based access, authenticator-app MFA and encrypted tenant credentials." },
];

function Field({ label, children, hint }) {
  return (
    <label className="auth-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export default function AuthPage({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const [form, setForm] = useState({ org_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const isRegister = mode === "register";
  const dest = location.state?.from || "/app";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finish = (session) => {
    setSession(session);
    toast.success(`Welcome, ${session.user.name.split(" ")[0]}`);
    navigate(session.user.must_change_password ? "/app/change-password" : dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isRegister) {
        finish(await authApi.register(form));
      } else {
        const res = await authApi.login({ email: form.email, password: form.password });
        if (res.mfa_required) {
          setChallenge(res.challenge);
        } else {
          finish(res);
        }
      }
    } catch (err) {
      toast.error(isRegister ? "Could not create your account" : "Sign-in failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authApi.mfaVerify({ challenge, code });
      if (res.used_recovery_code) toast.warning("You used a recovery code", { description: `${res.user.recovery_codes_left} recovery codes left.` });
      finish(res);
    } catch (err) {
      toast.error("Verification failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="auth-page">
      <aside className="auth-side">
        <Link to="/" className="brand" data-testid="brand-link">
          <span className="brand-mark">
            <Grid3x3 size={18} strokeWidth={2.4} aria-hidden="true" />
          </span>
          <span className="brand-text">
            Microsoft <b>License Hub</b>
          </span>
        </Link>
        <h1>
          Know exactly what your <span>Microsoft 365</span> licenses buy you.
        </h1>
        <ul className="auth-perks">
          {PERKS.map((p) => (
            <li key={p.title}>
              <span className="perk-ico">
                <p.icon size={16} />
              </span>
              <div>
                <b>{p.title}</b>
                <p>{p.text}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="auth-foot">
          The license matrix, lookup and cost calculator stay free and open - <Link to="/">browse without signing in</Link>.
        </p>
      </aside>

      <main className="auth-main">
        <div className="auth-card glass">
          {challenge ? (
            <form onSubmit={submitMfa} className="auth-form" data-testid="mfa-form">
              <div className="auth-head">
                <span className="auth-ico">
                  <KeyRound size={18} />
                </span>
                <h2>Two-step verification</h2>
                <p>Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p>
              </div>
              <Field label="Verification code">
                <Input value={code} onChange={(e) => setCode(e.target.value)} autoFocus inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" data-testid="mfa-code" />
              </Field>
              <button type="submit" className="tb-btn primary auth-submit" disabled={busy || code.trim().length < 6} data-testid="mfa-submit">
                {busy ? <Loader2 size={15} className="spin" /> : <ArrowRight size={15} />} Verify and sign in
              </button>
              <button type="button" className="tb-btn ghost" onClick={() => setChallenge("")} data-testid="mfa-back">
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={submit} className="auth-form" data-testid={isRegister ? "register-form" : "login-form"}>
              <div className="auth-head">
                <h2>{isRegister ? "Create your organization" : "Sign in"}</h2>
                <p>{isRegister ? "You'll be the first admin - invite your team afterwards." : "Welcome back. Sign in to your workspace."}</p>
              </div>
              {isRegister && (
                <>
                  <Field label="Organization name">
                    <Input value={form.org_name} onChange={set("org_name")} required minLength={2} placeholder="Contoso Ltd" data-testid="register-org" />
                  </Field>
                  <Field label="Your name">
                    <Input value={form.name} onChange={set("name")} required placeholder="Ada Lovelace" data-testid="register-name" />
                  </Field>
                </>
              )}
              <Field label="Work email">
                <Input type="email" value={form.email} onChange={set("email")} required autoComplete="email" placeholder="you@company.com" data-testid="auth-email" />
              </Field>
              <Field label="Password" hint={isRegister ? "At least 10 characters with upper & lower case and a number." : undefined}>
                <Input type="password" value={form.password} onChange={set("password")} required minLength={isRegister ? 10 : 1} autoComplete={isRegister ? "new-password" : "current-password"} data-testid="auth-password" />
              </Field>
              <button type="submit" className="tb-btn primary auth-submit" disabled={busy} data-testid="auth-submit">
                {busy ? <Loader2 size={15} className="spin" /> : <ArrowRight size={15} />} {isRegister ? "Create organization" : "Sign in"}
              </button>
              <p className="auth-switch">
                {isRegister ? (
                  <>
                    Already have an account? <Link to="/login" data-testid="to-login">Sign in</Link>
                  </>
                ) : (
                  <>
                    New here? <Link to="/register" data-testid="to-register">Create an organization</Link>
                  </>
                )}
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
